// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadFeaturedBrands from "@hardwoods/App/Handlers/FeaturedBrands/FeaturedBrands";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getFeaturedBrandsIds } from "@hardwoods/App/Store/Selectors/FeaturedBrandsSelectors";
import { headingFooterStyles } from "@hardwoods/Overrides/Widgets/Footer/FooterContainer";
import { constants } from "@hardwoods/XDL/constants";
import EventBus from "@hardwoods/XDL/Utilities/EventBus";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    pageSizeField = "pageSizeField",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.pageSizeField]: number;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const brandIds = getFeaturedBrandsIds(state);
    let brandGalleryParameter = {};
    let brandsState = {isLoading: false, value: [] as BrandModel[] | undefined};
    let shouldLoadBrandGallery;

    if (brandIds.length) {
        brandGalleryParameter = {
            brandIds: brandIds || [],
            randomize: false,
            pageSize: props.fields.pageSizeField,
            select: "id,detailPagePath,name",
        };
        brandsState = getBrandsDataView(state, brandGalleryParameter);
        shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;
    }

    return {
        brandIds,
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LinkListStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    brandItem?: LinkPresentationProps;
    listWrapper?: InjectableCss;
}

export const featuredBrandsStyles: LinkListStyles = {
    title: {
        css: css`
            ${headingFooterStyles}
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            padding: 0;
            &.hide {
                visibility: hidden;
            }
        `,
    },
};

const styles = featuredBrandsStyles;

const mapDispatchToProps = {
    loadFeaturedBrands,
    loadBrands,
};

const FeaturedBrands: React.FC<Props> = ({
    loadFeaturedBrands,
    loadBrands,
    brandGalleryParameter,
    shouldLoadBrandGallery,
    brandIds,
    brandsState,
    fields,
}) => {
    const { isLoading, value: brandList } = brandsState;
    useEffect(() => {
        loadFeaturedBrands({ pageSize: fields.pageSizeField });
    }, []);

    useEffect(() => {
        if (shouldLoadBrandGallery) {
            loadBrands(brandGalleryParameter);
        }

        setTimeout(() => {
            EventBus.dispatch(constants.customEvents.footerFeatureBrands, {});
        }, 500);
    }, [brandIds, brandGalleryParameter]);

    if (isLoading || !brandList || (brandList && brandList.length === 0)) {
        return null;
    }

    return (
        <StyledWrapper
        className="featureBrands"
        data-test-selector="featuredBrandsWrapper"
            {...styles.wrapper}
        >
            {fields.title && (
                <Typography as="h3" {...styles.title}>
                    {fields.title}
                </Typography>
            )}
            <StyledWrapper className="featureBrandsList" {...styles.listWrapper}>
                {brandList?.map(brand => {
                    return (
                        <StyledWrapper key={brand.id}>
                            <Link href={brand.detailPagePath} {...styles.brandItem}>
                                {brand.name}
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedBrands),
    definition: {
        displayName: "Featured Brands",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Featured Brands",
                fieldType: "General",
            },
            {
                name: fields.pageSizeField,
                displayName: "Page Size",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 6,
                min: 1,
            },
        ],
    },
};

export default widgetModule;
